import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useOrgStore } from '../store/orgStore';
import { decideAutoSwitch, type AutoSwitchDecision } from '../utils/orgSwitch';

/**
 * 조직 자동 전환 훅. CHG-149.
 *
 * 사용자가 다른 조직의 리소스에 접근했을 때 호출하면:
 *  - 활성 조직과 동일 → noop
 *  - 멤버이며 다름 → 활성 조직 전환 + 전체 react-query 캐시 invalidate + 토스트
 *  - 멤버 아님 → caller가 권한 안내 화면 표시 (status 반환)
 *
 * 단순 호출 패턴:
 *   const autoSwitch = useOrgAutoSwitch();
 *   useEffect(() => {
 *     if (!data) return;
 *     const result = autoSwitch(data.organizationId);
 *     if (result.status === 'not-a-member') setAccessDenied(true);
 *   }, [data, autoSwitch]);
 */
export function useOrgAutoSwitch() {
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const organizations = useOrgStore((s) => s.organizations);
  const setActiveOrgId = useOrgStore((s) => s.setActiveOrgId);
  const queryClient = useQueryClient();

  return useCallback(
    (targetOrgId: string | null | undefined): AutoSwitchDecision => {
      const memberOrgIds = new Set(organizations.map((o) => o.id));
      const decision = decideAutoSwitch(targetOrgId, activeOrgId, memberOrgIds);

      if (decision.status === 'switched' && decision.targetOrgId) {
        setActiveOrgId(decision.targetOrgId);
        // 활성 조직 변경 = 거의 모든 데이터가 다른 조직으로 → 전체 invalidate.
        // react-query는 lazy refetch라 화면에 보이는 query만 즉시 재요청 — 부담 작음.
        queryClient.invalidateQueries();
        const orgName = organizations.find((o) => o.id === decision.targetOrgId)?.name ?? '';
        toast.success(orgName ? `조직 '${orgName}'로 전환되었습니다` : '조직이 전환되었습니다');
      }

      return decision;
    },
    [activeOrgId, organizations, setActiveOrgId, queryClient],
  );
}
